import { Cookies as ReactCookies } from 'react-cookie';
import superagent from 'superagent/lib/client';
import globalSuperAgent from 'superagent-use';
import { getApiUrlAndSlug, getApiAltUrlAndSlug } from '../util/apiUrlUtility';
export const agent = globalSuperAgent(superagent);
const cookies = new ReactCookies();

/**
 * This class acts as a wrapper for superagent that contains all of the functions that interact
 * with the production APIs. Each method should be somewhat self-explanatory but
 * we have documented them nonetheless.
 */
export class ApiService {
  /**
   * @function get - Sends a GET request to the API endpoint
   * @param cookie - hallenToken, required for any API request
   * @param slug - API Endpoint to use
   * @param params - Any search params that may be used for the particular endpoint
   * @returns - data in the shape of referenced APIs response.
   */
  static async get(slug, params?: any, useAlt?: boolean) {
    agent.http2 = true;

    // Validate slug parameter - skip API call if invalid
    if (!slug || slug === undefined || slug === 'undefined') {
      console.warn('ApiService.get called with invalid slug, skipping API call:', slug);
      return {
        status: 200,
        data: [],
      };
    }

    //TODO temporary code to deal with the domain for electrofusion audit endpoints differing from the standard one, which generates a CORS error in the browser (that has to be dealt through with browser settings disabling that security check)
    //     also see corresponding prop below in .env file
    //const electrofusionUrl = slug.split('/').reduce((t, s)=> s.startsWith('ElectroFusion') || t, false)
    //const URL = `${electrofusionUrl ? process.env.HALLEN_API_ALT : process.env.HALLEN_API}${slug}${params ? params : ""}`
    let URL: string;
    if (slug?.startsWith('http')) {
      URL = `${slug}${params ? params : ''}`;
    } else {
      const { baseUrl, slug: modifiedSlug } = useAlt ? getApiAltUrlAndSlug(slug) : getApiUrlAndSlug(slug);
      URL = `${baseUrl}${modifiedSlug}${params ? params : ''}`;
    }
    //const URL = `${process.env.HALLEN_API}${slug}${params ? params : ""}`
    const cookie = cookies.get('hallenToken');
    let response;
    await agent
      .get(URL)
      .type('json')
      .set('Accept', 'application/json')
      .set('Authorization', 'Bearer ' + cookie)
      .set('Ocp-Apim-Subscription-Key', `${process.env.HALLEN_KEY}`)
      .then(res => {
        response = { ...res, data: res?.body };
      })
      .catch(error => {
        if (error && error.response) {
          response = { ...error.response, data: error.response?.body };
        } else {
          response = ['An unknown error occurred.'];
        }
      });
    return response;
  }

  /**
   * @function put - Sends a PUT request to the API endpoint
   * @param cookie - hallenToken, required for any API request
   * @param slug - API Endpoint to use
   * @param payload - The request body
   * @param progressCallback - callback to set progress bar percentage
   * @returns - data in the shape of referenced APIs response.
   */
  static async put(slug, payload, progressCallback?: any, useAlt?: boolean) {
    let URL: string;
    if (slug?.startsWith('http')) {
      URL = slug;
    } else {
      const { baseUrl, slug: modifiedSlug } = useAlt ? getApiAltUrlAndSlug(slug) : getApiUrlAndSlug(slug);
      URL = `${baseUrl}${modifiedSlug}`;
    }
    const cookie = cookies.get('hallenToken');
    let response;
    await agent
      .put(URL)
      .send(payload)
      .type('json')
      .set('Accept', 'application/json')
      .set('Authorization', 'Bearer ' + cookie)
      .set('Ocp-Apim-Subscription-Key', `${process.env.HALLEN_KEY}`)
      .on('progress', function (e) {
        console.log(e.loaded);
      })
      .on('progress', function (progressEvent) {
        progressCallback ? progressCallback(Math.floor((progressEvent.loaded / progressEvent.total) * 100)) : console.log(Math.floor((progressEvent.loaded / progressEvent.total) * 100));
      })
      .then(res => {
        response = { ...res, data: res?.body };
        this.CookieUpdate(response);
      })
      .catch(error => {
        response = { ...error.response, data: error.response?.body };
      });
    return response;
  }

  /**
   * @function post - Sends a POST request to the API endpoint
   * @param cookie - hallenToken, required for any API request
   * @param slug - API Endpoint to use
   * @param payload - The request body
   * @param progressCallback - callback to set progress bar percentage
   * @returns - data in the shape of referenced APIs response.
   */
  static async post(slug, payload?: any, progressCallback?: any, useAlt?: boolean) {
    let URL: string;
    if (slug?.startsWith('http')) {
      URL = slug;
    } else {
      const { baseUrl, slug: modifiedSlug } = useAlt ? getApiAltUrlAndSlug(slug) : getApiUrlAndSlug(slug);
      URL = `${baseUrl}${modifiedSlug}`;
    }
    const cookie = cookies.get('hallenToken');
    let response;

    // Detect if payload is FormData (for file uploads)
    const isFormData = payload instanceof FormData;

    const request = agent
      .post(URL)
      .send(payload)
      .set('Accept', 'application/json')
      .set('Authorization', 'Bearer ' + cookie)
      .set('Ocp-Apim-Subscription-Key', `${process.env.HALLEN_KEY}`)
      .on('progress', function (progressEvent) {
        progressCallback ? progressCallback(Math.floor((progressEvent.loaded / progressEvent.total) * 100)) : console.log(Math.floor((progressEvent.loaded / progressEvent.total) * 100));
      });

    // Only set Content-Type to 'json' if NOT FormData
    // For FormData, let the browser set the correct multipart/form-data with boundary
    if (!isFormData) {
      request.type('json');
    }

    await request
      .then(res => {
        response = { ...res, data: res?.body };
        this.CookieUpdate(response);
      })
      .catch(error => {
        response = { ...error.response, data: error.response?.body };
      });
    return response;
  }

  /**
   * @function delete - Sends a DELETE request to the API endpoint
   * @param cookie - hallenToken, required for any API request
   * @param slug - API Endpoint to use
   * @param payload - The request body
   * @returns - data in the shape of referenced APIs response.
   */
  static async delete(slug, payload) {
    let URL: string;
    if (slug?.startsWith('http')) {
      URL = slug;
    } else {
      const { baseUrl, slug: modifiedSlug } = getApiUrlAndSlug(slug);
      URL = `${baseUrl}${modifiedSlug}`;
    }
    const cookie = cookies.get('hallenToken');
    let response;
    await agent
      .del(URL, Array.isArray(payload) ? payload : [payload])
      .type('json')
      .set('Accept', 'application/json')
      .set('Authorization', 'Bearer ' + cookie)
      .set('Ocp-Apim-Subscription-Key', `${process.env.HALLEN_KEY}`)
      .then(res => {
        response = { ...res, data: res?.body };
        this.CookieUpdate(response);
      })
      .catch(error => {
        response = { ...error.response, data: error.response?.body };
      });
    return response;
  }

  static CookieUpdate(res) {
    if (res && res.status === 200 && res.data && !!res.data.authToken) {
      cookies.set('hallenToken', res.data.authToken, {
        maxAge: 3600,
        path: '/',
      });
    }
  }
}
